import React, { useCallback, useState, Fragment, useRef } from 'react';
import { MenuItem, Tooltip } from '@material-ui/core';
import { Space } from '../Space/Space';
import { useParams } from 'react-router';
import { MenuDropdown } from './MenuDropdown';

type MenuItem = {
  menuId: number;
  label: string;
  url: string;
  target: string;
  parentMenuId?: number;
  menuItemsRef: any;
  [key: string]: any;
};

const filterParentMenuItems = (item: MenuItem) => !item.parentMenuId;

export default function DesktopMenu(props: any) {
  const [anchorEl, setAnchorEl] = useState<{ [menuId: number]: HTMLElement | null }>({});

  const { id } = useParams();

  const logoRef = useRef<any>();
  const menuRef = useRef<any>();

  const handleClick = (event: any, menuId: number) => {
    setAnchorEl(prev => ({ ...prev, [menuId]: event.currentTarget }));
  };

  const handleClose = (menuId: number) => {
    setAnchorEl(prev => ({ ...prev, [menuId]: null }));
  };

  const handleNavigate = (url: string, target: string, menuId: number) => {
    navigate(url, target);
    handleClose(menuId);
  };

  const { menuItems, navigate, menuLayoutParts, current, contentLayout, logoSrc, settings, moduleWidth } = props;
  const { showLogo = false, logoSource = '', showLinkTooltip = false, logoLink } = settings;

  const mapper = useCallback(
    (part: string, index: number) => {
      let cols = part.replace(/\D+/, '');

      if (part?.includes('*')) {
        const fixedCols = menuLayoutParts.reduce((acc: number, curr: string) => {
          if (curr.startsWith('L') && (!showLogo || !logoSrc)) return acc;
          const numString = curr.replace(/\D+/, '');
          if (numString) return acc + parseInt(numString);
          return acc;
        }, 0);
        cols = (moduleWidth - fixedCols).toString();
      }

      if (part.startsWith('S')) {
        return (
          <div key={index} style={{ width: `calc((100vw) * (${cols} / 12))` }}>
            <Space settings={{ divider: false }} />
          </div>
        );
      }

      if (part.startsWith('L') && showLogo && logoSrc) {
        const imageStyle = {
          height: logoRef.current?.naturalHeight > logoRef.current?.naturalWidth ? '100%' : 'auto',
          width: logoRef.current?.naturalHeight < logoRef.current?.naturalWidth ? '100%' : '100%',
          aspectRatio: `${logoRef.current?.naturalWidth} / ${logoRef.current?.naturalHeight}`,
        };

        const wrapStyle = {
          aspectRatio: `${logoRef.current?.naturalWidth} / ${logoRef.current?.naturalHeight}`,
          width: `calc((100vw) * (${cols} / 12))`,
        };

        return (
          <div key={`menu_logo_${index}`} className="menu-logo-wrap" style={wrapStyle}>
            {logoLink ? (
              <a href={logoLink}>
                <img ref={logoRef} className="menu-logo" style={imageStyle} src={logoSource} />
              </a>
            ) : (
              <img ref={logoRef} className="menu-logo" style={imageStyle} src={logoSource} />
            )}
          </div>
        );
      }

      if (part.startsWith('M')) {
        return (
          <header key={index} style={{ width: `calc((100vw) * (${cols} / 12))` }}>
            <div className="menu-items" ref={props?.menuItemsRef}>
              {menuItems.filter(filterParentMenuItems).map(({ menuId, url, layoutCode, label, target }: MenuItem) => (
                <Fragment key={`desktop_${url}_${menuId}`}>
                  {menuItems.some(({ parentMenuId }) => parentMenuId === menuId) ? (
                    <MenuDropdown
                      menuId={menuId}
                      label={label}
                      menuItems={menuItems}
                      handleNavigate={handleNavigate}
                      current={current}
                      contentLayout={contentLayout}
                    />
                  ) : (
                    <Tooltip title={url} disableFocusListener disableTouchListener disableHoverListener={!showLinkTooltip}>
                      {url?.startsWith('#') ? (
                        <a href={url} className={`cms-menu-item no-underline text-inherit`}>
                          {label}
                        </a>
                      ) : (
                        <div onClick={() => navigate(url, target)} className={`cms-menu-item ${id === layoutCode ? 'active' : ''}`}>
                          {label}
                        </div>
                      )}
                    </Tooltip>
                  )}
                </Fragment>
              ))}
            </div>
          </header>
        );
      }
    },
    [menuLayoutParts, moduleWidth, showLogo, logoSrc, anchorEl, menuItems, id, props?.menuItemsRef]
  );

  if (!menuItems?.length) return null;

  return menuLayoutParts.map(mapper);
}
